/**
 * Suite run detail view showing per-workflow run comparison and cache metrics.
 *
 * @module pages/WorkflowSuiteDetail
 * @see docs/api/services/benchmark.md
 */
import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Card,
  CardContent,
  DataTable,
  LoadingOrbit,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { ArrowLeft, ArrowSquareOut, Play, Trash } from '@phosphor-icons/react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import type { BenchmarkWorkflowRunMetrics } from '@chatsdet/types';
import {
  useWorkflowSuiteDetailQuery,
  useDeleteWorkflowSuiteMutation,
  runStatusVariants,
  runStatusLabels,
  workflowRunStatusVariants,
  workflowRunStatusLabels,
  formatDateTime,
} from '../useBenchmarkData';
import {
  formatDuration,
  formatTokenCount,
  stripBenchmarkPrefix,
} from './useWorkflowSuiteTransforms';
import { WorkflowRunDetail } from './WorkflowRunDetail';
import { ArmComparisonReport } from '../benchmark/ArmComparisonReport';

const workflowRunColumns: ColumnDef<StockFeatures, BenchmarkWorkflowRunMetrics>[] = [
  {
    accessorKey: 'runNumber',
    header: 'Run #',
    meta: { flex: 0.4, maxWidth: 80 },
    cell: ({ row }) => <Text>{row.original.runNumber}</Text>,
  },
  {
    accessorKey: 'workflowName',
    header: 'Workflow',
    meta: { flex: 2 },
    cell: ({ row }) => (
      <Flex align="center" gap="sm" className="min-w-0">
        <Text weight="medium" className="break-words" title={row.original.workflowName}>
          {stripBenchmarkPrefix(row.original.workflowName)}
        </Text>
        <a
          href={`/flows/${row.original.sourceWorkflowId}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Open source flow"
          className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <ArrowSquareOut size={14} />
        </a>
        {row.original.workflowRunId && (
          <a
            href={`/flows/${row.original.copyWorkflowId}?runId=${row.original.workflowRunId}`}
            target="_blank"
            rel="noopener noreferrer"
            title="View benchmark run"
            className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <Play size={14} />
          </a>
        )}
      </Flex>
    ),
  },
  {
    accessorKey: 'isCached',
    header: 'Cached',
    cell: ({ row }) => (
      <TagBadge
        name={row.original.isCached ? 'Yes' : 'No'}
        color={badgeVariantToTagColor(row.original.isCached ? 'success' : 'secondary')}
        hideDot
      />
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TagBadge
        name={workflowRunStatusLabels[row.original.status]}
        color={badgeVariantToTagColor(workflowRunStatusVariants[row.original.status])}
        hideDot
      />
    ),
  },
  {
    accessorKey: 'durationMs',
    header: 'Duration',
    cell: ({ row }) => <Text>{formatDuration(row.original.durationMs)}</Text>,
  },
  {
    accessorKey: 'totalTokens',
    header: 'Tokens',
    cell: ({ row }) => <Text>{formatTokenCount(row.original.totalTokens)}</Text>,
  },
  {
    id: 'nodes',
    header: 'Nodes',
    cell: ({ row }) => {
      const { successfulNodes, totalNodes } = row.original;
      if (totalNodes === null || totalNodes === undefined)
        return <Text className="text-muted-foreground">—</Text>;
      return (
        <Text>
          <span className="text-success">{successfulNodes ?? 0}</span>
          <span className="text-muted-foreground">/{totalNodes}</span>
        </Text>
      );
    },
  },
  {
    accessorKey: 'errorMessage',
    header: 'Error',
    cell: ({ row }) => {
      const msg = row.original.errorMessage;
      if (!msg) return <Text className="text-muted-foreground">—</Text>;
      return (
        <Text size="sm" className="text-destructive truncate max-w-[300px]" title={msg}>
          {msg}
        </Text>
      );
    },
  },
];

interface WorkflowSuiteDetailProps {
  suiteId: string;
  onBack: () => void;
}

export function WorkflowSuiteDetail({ suiteId, onBack }: WorkflowSuiteDetailProps) {
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const query = useWorkflowSuiteDetailQuery(suiteId);
  const deleteMutation = useDeleteWorkflowSuiteMutation();

  // All hooks must be called before any early returns (Rules of Hooks)
  const workflowRuns = query.data?.workflowRuns ?? [];

  const cacheStats = React.useMemo(() => {
    let hits = 0,
      l2Recoveries = 0,
      ejections = 0,
      misses = 0;
    let peakInput = 0,
      ctxWindow = 0;
    for (const wr of workflowRuns) {
      if (!wr.nodeMetrics) continue;
      for (const nm of wr.nodeMetrics) {
        const outcome = nm.cacheOutcome;
        const source = nm.cacheHitSource;
        if (outcome === 'hit') {
          if (source === 'recovery' || source === 'l2_gap') l2Recoveries++;
          else hits++;
        } else if (outcome === 'fallback') ejections++;
        else if (outcome === 'miss' || outcome === 'skipped') misses++;
        if (nm.maxInputTokens && nm.maxInputTokens > peakInput) {
          peakInput = nm.maxInputTokens;
          ctxWindow = nm.contextWindowTokens ?? 0;
        }
      }
    }
    return { hits, l2Recoveries, ejections, misses, peakInput, ctxWindow };
  }, [workflowRuns]);

  if (query.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-10">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  const detail = query.data;
  if (!detail) {
    return <Text className="text-muted-foreground">Suite not found.</Text>;
  }

  // If a run is selected, show run detail
  if (selectedRunId) {
    return <WorkflowRunDetail runId={selectedRunId} onBack={() => setSelectedRunId(null)} />;
  }

  const { run } = detail;
  const isActive = run.status === 'running' || run.status === 'pending';
  const meta = run.metadata as Record<string, unknown> | null;
  const isAdHoc = meta?.adHoc === true;
  const totalWorkflows = (meta?.totalWorkflows as number) ?? 0;
  const completedWorkflows = (meta?.completedWorkflows as number) ?? 0;
  const failedWorkflows = (meta?.failedWorkflows as number) ?? 0;

  // Aggregate metrics from workflow runs
  const totalTokens = workflowRuns.reduce((sum, wr) => sum + (wr.totalTokens ?? 0), 0);
  const totalDurationMs = workflowRuns.reduce((sum, wr) => sum + (wr.durationMs ?? 0), 0);

  return (
    <Box className="space-y-4">
      {/* Header */}
      <Flex align="center" gap="md">
        <button
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <Heading level={4}>{isAdHoc ? 'A/B Benchmark Run' : 'Suite Run'}</Heading>
          <Flex gap="sm" align="center" className="mt-1">
            <TagBadge
              name={runStatusLabels[run.status]}
              color={badgeVariantToTagColor(runStatusVariants[run.status])}
              hideDot
            />
            <Text size="sm" className="text-muted-foreground">
              {formatDateTime(run.startedAt)}
            </Text>
          </Flex>
        </div>
        <button
          onClick={() => {
            if (
              window.confirm('Delete this suite run and its copy workflows? This cannot be undone.')
            ) {
              deleteMutation.mutate(suiteId, { onSuccess: onBack });
            }
          }}
          disabled={deleteMutation.isPending || isActive}
          className="text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
          title={isActive ? 'Cannot delete a running or pending suite' : 'Delete suite run'}
        >
          <Trash size={20} />
        </button>
      </Flex>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card padding="sm">
          <CardContent>
            <Text size="xs" className="text-muted-foreground">
              Workflows
            </Text>
            <Text size="lg" weight="bold">
              {totalWorkflows}
            </Text>
          </CardContent>
        </Card>
        <Card padding="sm">
          <CardContent>
            <Text size="xs" className="text-muted-foreground">
              Passed
            </Text>
            <Text size="lg" weight="bold" className="text-success">
              {completedWorkflows}
            </Text>
          </CardContent>
        </Card>
        <Card padding="sm">
          <CardContent>
            <Text size="xs" className="text-muted-foreground">
              Failed
            </Text>
            <Text size="lg" weight="bold" className={failedWorkflows > 0 ? 'text-destructive' : ''}>
              {failedWorkflows}
            </Text>
          </CardContent>
        </Card>
        <Card padding="sm">
          <CardContent>
            <Text size="xs" className="text-muted-foreground">
              Total Tokens
            </Text>
            <Text size="lg" weight="bold">
              {formatTokenCount(totalTokens)}
            </Text>
          </CardContent>
        </Card>
        <Card padding="sm">
          <CardContent>
            <Text size="xs" className="text-muted-foreground">
              Total Duration
            </Text>
            <Text size="lg" weight="bold">
              {formatDuration(totalDurationMs)}
            </Text>
          </CardContent>
        </Card>
      </div>

      {/* Cache stats */}
      {(cacheStats.hits > 0 || cacheStats.l2Recoveries > 0 || cacheStats.ejections > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card padding="sm">
            <CardContent>
              <Text size="xs" className="text-muted-foreground">
                Cache Hits
              </Text>
              <Text size="lg" weight="bold" className="text-success">
                {cacheStats.hits}
              </Text>
            </CardContent>
          </Card>
          <Card padding="sm">
            <CardContent>
              <Text size="xs" className="text-muted-foreground">
                L2 Recoveries
              </Text>
              <Text size="lg" weight="bold" className="text-warning">
                {cacheStats.l2Recoveries}
              </Text>
            </CardContent>
          </Card>
          <Card padding="sm">
            <CardContent>
              <Text size="xs" className="text-muted-foreground">
                Ejections
              </Text>
              <Text
                size="lg"
                weight="bold"
                className={cacheStats.ejections > 0 ? 'text-destructive' : ''}
              >
                {cacheStats.ejections}
              </Text>
            </CardContent>
          </Card>
          <Card padding="sm">
            <CardContent>
              <Text size="xs" className="text-muted-foreground">
                Cache Misses
              </Text>
              <Text size="lg" weight="bold">
                {cacheStats.misses}
              </Text>
            </CardContent>
          </Card>
          {cacheStats.peakInput > 0 && cacheStats.ctxWindow > 0 && (
            <Card padding="sm">
              <CardContent>
                <Text size="xs" className="text-muted-foreground">
                  Peak Context
                </Text>
                <Text
                  size="lg"
                  weight="bold"
                  className={
                    cacheStats.peakInput / cacheStats.ctxWindow > 0.8
                      ? 'text-destructive'
                      : cacheStats.peakInput / cacheStats.ctxWindow > 0.6
                        ? 'text-warning'
                        : 'text-success'
                  }
                >
                  {Math.round((cacheStats.peakInput / cacheStats.ctxWindow) * 100)}%
                </Text>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Ad-hoc A/B arm comparison report */}
      {isAdHoc && (
        <Card padding="md">
          <CardContent>
            <Heading level={5} className="mb-3">
              Arm Comparison
            </Heading>
            <ArmComparisonReport suiteId={suiteId} />
          </CardContent>
        </Card>
      )}

      {/* Workflow runs table */}
      <Card padding="md">
        <CardContent>
          <Heading level={5} className="mb-3">
            Workflow Runs
          </Heading>
          <DataTable
            columns={workflowRunColumns}
            data={workflowRuns}
            onRowClick={(row) => setSelectedRunId(row.id)}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
